import { useCallback, useRef, useState, useSyncExternalStore } from 'react'

const COUNTDOWN_PATTERN = /\{countdown:(\d+)\}|\{countdown-until:([^}]+)\}/g
const SECONDS_PER_HOUR = 3600
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR

function formatCountdown(totalSeconds: number, showHours: boolean): string {
    const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR)
    const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / 60)
    const seconds = totalSeconds % 60

    if (showHours) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`
}

function formatRollingCountdown(minutes: number, tick: number): string {
    if (minutes <= 0) return '0:00'
    const totalSeconds = minutes * 60
    const remaining = totalSeconds - (tick % (totalSeconds + 1))
    return formatCountdown(remaining, minutes >= 60)
}

function formatDeadlineCountdown(deadlineMs: number, nowMs: number): string {
    const remaining = Math.max(0, Math.ceil((deadlineMs - nowMs) / 1000))
    const days = Math.floor(remaining / SECONDS_PER_DAY)
    const time = formatCountdown(
        remaining % SECONDS_PER_DAY,
        days > 0 || remaining >= SECONDS_PER_HOUR,
    )
    return days > 0 ? `${days}d ${time}` : time
}

function renderCountdownText(
    text: string,
    tick: number,
    nowMs: number | null,
): string {
    return text.replace(
        COUNTDOWN_PATTERN,
        (token: string, minutes?: string, deadline?: string) => {
            if (minutes !== undefined) {
                return formatRollingCountdown(parseInt(minutes, 10), tick)
            }
            const deadlineMs = Date.parse(deadline ?? '')
            if (Number.isNaN(deadlineMs)) return token
            if (nowMs === null) return ''
            return formatDeadlineCountdown(deadlineMs, nowMs)
        },
    )
}

function useClock(enabled: boolean): number | null {
    const clock = useRef<number | null>(null)
    const subscribe = useCallback(
        (onChange: () => void) => {
            if (!enabled) return () => {}
            const update = () => {
                clock.current = Date.now()
                onChange()
            }
            update()
            const interval = setInterval(update, 1000)
            return () => clearInterval(interval)
        },
        [enabled],
    )
    return useSyncExternalStore(
        subscribe,
        () => clock.current,
        () => null,
    )
}

export const CountdownText: React.FC<{ text: string }> = ({ text }) => {
    const [trackedText, setTrackedText] = useState(text)
    const [startMs, setStartMs] = useState(() => Date.now())
    const hasCountdown = !!text.match(COUNTDOWN_PATTERN)
    const nowMs = useClock(hasCountdown)

    if (text !== trackedText) {
        setTrackedText(text)
        setStartMs(Date.now())
    }

    if (!hasCountdown) return <>{text}</>

    const tick =
        nowMs === null ? 0 : Math.max(0, Math.floor((nowMs - startMs) / 1000))
    return <>{renderCountdownText(text, tick, nowMs)}</>
}
